import { sortBy } from 'lodash';
import {
  TenderSubFolderCoreVM,
  DbAttachment,
  DbShortFolderItem,
  DbTender,
  DbTenderFolder,
  DbTenderUser,
  TenderCoreRegVM,
  TenderRegistrationVM,
} from '~src/types/db';
import { appLog } from '~src/utils/log';
import { apiCweb } from './api-cweb';
import { LoadFoldersParams } from './folders';
import { getAttachment, GetAttachmentResponse } from '~src/utils/apiUtils';
import {
  getStorageAdminDownload,
  getStorageTimestamp,
} from '~src/utils/sessionstorageQueries';

export interface LoadTenderUsersParams {
  projectId: string;
}

export type LoadTendersParams = LoadTenderUsersParams & { tenderId: string };

export const loadTenderUsers = async (
  _,
  { projectId }: LoadTenderUsersParams
) => {
  appLog('loadTenderUsers ->', { projectId });
  const res = await apiCweb.get<DbTenderUser[]>(
    `/project_settings/${projectId}/tender_users`,
    {}
  );

  return res.data;
};

export const loadArchiveUsers = async (
  _,
  { projectId }: LoadTenderUsersParams
) => {
  appLog('loadArchiveUsers ->', { projectId });
  const res = await apiCweb.get<DbTenderUser[]>(
    `/project_settings/${projectId}/archive_users`,
    {}
  );

  return res.data;
};

interface AddTenderUserParams {
  userId: string;
  action: string;
  projectId: string;
}

export const addTenderUser = async ({
  userId,
  action,
  projectId,
}: AddTenderUserParams) => {
  appLog('addTenderUser -> ', {
    userId,
    action,
    projectId,
  });

  const res = await apiCweb.post(
    `/project_settings/${projectId}/tender_users/${userId}?action=${action}`,
    {}
  );

  return res.data;
};

type AddTenderParams = {
  data: {
    title: string;
    reference: string;
    description?: string | null;
    active_from: number;
    active_until: number;
  };
  projectId: string;
};

export const addTender = async ({ data, projectId }: AddTenderParams) => {
  appLog('addTender -> ', {
    data,
    projectId,
  });

  const res = await apiCweb.post(`/projects/${projectId}/tenders`, data);

  return res.data;
};

interface RemoveTenderUserParams {
  userId: string;
  projectId: string;
}

export const removeTenderUser = async ({
  userId,
  projectId,
}: RemoveTenderUserParams) => {
  appLog('removeTenderUser -> ', {
    userId,
    projectId,
  });

  const res = await apiCweb.delete(
    `/project_settings/${projectId}/tender_users/${userId}`,
    {}
  );

  return res.data;
};

interface ShareTenderParams {
  tenderId: string;
  projectId: string;
  emails: string[];
}

export const shareTender = async ({
  projectId,
  tenderId,
  emails,
}: ShareTenderParams) => {
  const formattedEmails = emails.map((email) => ({ email: email }));
  appLog('shareTender ->', { projectId, emails });
  const res = await apiCweb.post(
    `/projects/${projectId}/tenders/${tenderId}/share_keys`,
    formattedEmails,
    {}
  );
  return res.data;
};

export const loadTenderRegistrationDataById = async ({
  projectId,
  tender_id,
}: Pick<
  LoadFoldersParams,
  'projectId' | 'tender_id'
>): Promise<TenderRegistrationVM> => {
  appLog('loadTenderRegistrationDataById ->', { projectId, tender_id });
  const res = await apiCweb.get<TenderRegistrationVM>(
    `/projects/${projectId}/tenders/${tender_id}`
  );

  return res.data;
};

export const loadTendersTenderFolders = async (
  key,
  {
    projectId,
    favourites,
    before,
    after,
    search_name,
    search_description,
    as_admin,
    show_deleted,
    tender_id,
  }: LoadFoldersParams
) => {
  appLog('loadTenderFolders ->', { projectId, favourites });
  const res = await apiCweb.get<DbTenderFolder[]>(
    `/projects/${projectId}/tenders/${tender_id}/tender_folders`,
    {
      params: {
        favorites: favourites,
        before: before,
        search_name: search_name,
        search_description: search_description,
        as_admin: as_admin,
        show_deleted: show_deleted === undefined ? as_admin : show_deleted,
        after: after,
      },
    }
  );

  const res2 = await apiCweb.get<DbTenderFolder[]>(
    `/projects/${projectId}/tenders/${tender_id}/attachments`,
    {
      params: {
        favorites: favourites,
        before: before,
        search_name: search_name,
        search_description: search_description,
        as_admin: as_admin,
        show_deleted: show_deleted === undefined ? as_admin : show_deleted,
        after: after,
      },
    }
  );

  const sorted = sortBy(res.data, [
    (folder) => (folder.name || '').toUpperCase(),
  ]);
  return sorted;
};

export const loadTendersTenderAttachments = async (
  key,
  {
    projectId,
    favourites,
    before,
    after,
    search_name,
    search_description,
    as_admin,
    show_deleted,
    tender_id,
  }: LoadFoldersParams
) => {
  appLog('loadTenderAttachments ->', { projectId, favourites });

  const res = await apiCweb.get<DbAttachment[]>(
    `/projects/${projectId}/tenders/${tender_id}/attachments`,
    {
      params: {
        favorites: favourites,
        before: before,
        search_name: search_name,
        search_description: search_description,
        as_admin: as_admin,
        show_deleted: show_deleted === undefined ? as_admin : show_deleted,
        after: after,
      },
    }
  );

  const sorted = sortBy(res.data, [
    (folder) => (folder.name || '').toUpperCase(),
  ]);
  return sorted;
};

interface GetTenderAttachmentParams {
  tenderId: string;
  projectId: string;
  attachmentId: string;
}

export type GetTenderAttachmentResponse = GetAttachmentResponse;

export const getTenderAttachment = async ({
  tenderId,
  attachmentId,
  projectId,
}: GetTenderAttachmentParams): Promise<GetTenderAttachmentResponse> => {
  appLog('getTenderAttachment -> ', {
    tenderId,
    attachmentId,
    projectId,
  });

  return getAttachment({
    url: `/projects/${projectId}/tenders/${tenderId}/attachments/${attachmentId}/location`,
  });
};

export type GetTenderVersionedFileParams = GetTenderAttachmentParams & {
  versionedFileId: string | null;
  rootFolderId: string;
  currentFolderId: string;
};

export const getTenderVersionedFile = async ({
  tenderId,
  attachmentId,
  projectId,
  versionedFileId,
  rootFolderId,
  currentFolderId,
}: GetTenderVersionedFileParams): Promise<GetTenderAttachmentResponse> => {
  appLog('getTenderVersionedFile -> ', {
    tenderId,
    attachmentId,
    projectId,
    versionedFileId,
    rootFolderId,
    currentFolderId,
  });

  return getAttachment({
    url: `/projects/${projectId}/tenders/${tenderId}/tender_folders/${rootFolderId}/folders/${currentFolderId}/versioned_files/${versionedFileId}/files/${attachmentId}/location`,
  });
};

export type GetTenderVersionedFileLocationParams = {
  projectId: number;
  tenderId: number;
  rootFolderId: number;
  folderId: number;
  versionedFileId: number;
  fileGuid: string;
};

export const getTenderVersionedFileLocationTwo = async ({
  projectId,
  tenderId,
  rootFolderId,
  folderId,
  versionedFileId,
  fileGuid,
}: GetTenderVersionedFileLocationParams): Promise<GetTenderAttachmentResponse> => {
  appLog('getTenderVersionedFileLocationTwo -> ', {
    projectId,
    tenderId,
    rootFolderId,
    folderId,
    versionedFileId,
    fileGuid,
  });
  try {
    return getAttachment({
      url: `/projects/${projectId}/tender_folders/${rootFolderId}/folders/${folderId}/versioned_files/${versionedFileId}/files/${fileGuid}/location`,
    });
  } catch (error) {
    appLog(error);
  }
};

interface newFileType {
  file_id: string;
  file_name: string;
  file_size: number;
  folder_id: number;
  using_key: string;
}

export const newSingleTenderDownload = async (
  key: string,
  { projectId, tenderId, file_ids }
): Promise<newFileType[]> => {
  appLog('newSingleTenderDownload #1 ->', {
    projectId,
    tenderId,
    file_ids,
  });
  try {
    const res = await apiCweb.get(
      `/projects/${projectId}/tenders/${tenderId}/attachments/file_download`,
      {
        params: {
          file_ids: Array.isArray(file_ids) ? file_ids.join(',') : file_ids,
        },
        timeout: 30000,
      }
    );

    appLog(`newSingleTenderDownload LOG #2 -> `, res.data);
    return res.data;
  } catch (error) {
    console.error(`Failed to load file: ${file_ids}`, error);
    return [];
  }
};

export const newSingleTenderFolderFileDownload = async (
  key: string,
  { projectId, tenderId, rootFolderId, folderId, registration_ids, serial }
): Promise<newFileType[]> => {
  appLog('newSingleTenderFolderFileDownload #1 ->', {
    projectId,
    tenderId,
    rootFolderId,
    folderId,
    registration_ids,
    serial,
  });
  try {
    const res = await apiCweb.get<newFileType[]>(
      `/projects/${projectId}/tenders/${tenderId}/tender_folders/${rootFolderId}/folders/${folderId}/versioned_files/file_download`,
      {
        params: {
          recursive: true,
          registration_ids: Array.isArray(registration_ids)
            ? registration_ids.join(',')
            : registration_ids,
          skip: 0,
          serial: serial,
        },
        timeout: 30000,
      }
    );

    appLog(`newSingleTenderFolderFileDownload LOG #2 -> `, res.data);
    return res.data;
  } catch (error) {
    console.error(`Failed to load file: ${registration_ids}`, error);
    return [];
  }
};

export const getTendersURLs = async (
  projectId: number,
  tenderId: number,
  using_keys: string[]
) => {
  appLog('getTendersURLs #1 ->', {
    projectId,
    tenderId,
    using_keys,
  });
  try {
    const res = await apiCweb.post(
      `/projects/${projectId}/tenders/${tenderId}/attachments/batch_download_urls`,
      { using_keys },
      { timeout: 30000 }
    );

    appLog(`getTendersURLs LOG #2 ->`, res.data);
    return res.data;
  } catch (error) {
    return [];
  }
};

export const getTenderVersionedFileLocation = async ({
  tenderId,
  attachmentId,
  projectId,
  versionedFileId,
  rootFolderId,
  currentFolderId,
}: GetTenderVersionedFileParams) => {
  appLog('getTenderVersionedFileLocaation -> ', {
    tenderId,
    attachmentId,
    projectId,
    versionedFileId,
    rootFolderId,
    currentFolderId,
  });

  const locationRequestResult = await apiCweb.head(
    `/projects/${projectId}/tenders/${tenderId}/tender_folders/${rootFolderId}/folders/${currentFolderId}/versioned_files/${versionedFileId}/files/${attachmentId}/location`
  );

  return locationRequestResult.headers.location;
};

interface LinkUnlinkTendereFolderParams {
  tenderId: string;
  projectId: string;
  action: string;
  tenderFolderId: string;
}

export const linkUnlinkTendereFolder = async ({
  tenderId,
  action,
  projectId,
  tenderFolderId,
}: LinkUnlinkTendereFolderParams) => {
  appLog('linkUnlinkTendereFolder -> ', {
    tenderId,
    action,
    projectId,
    tenderFolderId,
  });

  const res = await apiCweb.put(
    `/projects/${projectId}/tenders/${tenderId}?action=${action}&folder_id=${tenderFolderId}`,
    {}
  );

  return res.data;
};

export const loadTendersRegistrationCoreTenders = async (
  _,
  { projectId, tenderId }: LoadTendersParams
): Promise<TenderCoreRegVM> => {
  appLog('loadTendersRegistrationCoreTenders ->', { projectId, tenderId });

  const res = await apiCweb.get<TenderCoreRegVM>(
    `/projects/${projectId}/tenders/${tenderId}/core_registration`
  );

  return res.data;
};

export const loadTendersRegistrationCoreFolders = async (
  _,
  { projectId, tenderId }: LoadTendersParams
): Promise<DbShortFolderItem[]> => {
  appLog('loadTendersRegistrationCoreFolders ->', { projectId, tenderId });

  const res = await apiCweb.get<DbShortFolderItem[]>(
    `/projects/${projectId}/tenders/${tenderId}/core_folders`
  );

  return res.data;
};

export type LoadTenderFolderParams = LoadTendersParams & {
  folderId: string;
};

export const loadTendersRegistrationCoreTenderFolders = async ({
  projectId,
  tenderId,
  folderId,
}: LoadTenderFolderParams): Promise<TenderSubFolderCoreVM> => {
  appLog('loadTendersRegistrationCoreTenderFolders ->', {
    projectId,
    tenderId,
    folderId,
  });

  const res = await apiCweb.get<TenderSubFolderCoreVM>(
    `/projects/${projectId}/tenders/${tenderId}/core_folders/${folderId}`
  );

  return res.data;
};

export interface TenderCoreVoucher {
  id: number;
  name: string;
  description: string | null;
  unit_count: number | null;
  unit_type: number | null;
  is_voucher_group: boolean;
  contract_id: number | null;
  sort: number | null;
  voucher_group_option: {
    voucher_group_contract_id: number;
    sub_voucher_ids: number[];
  };
  offer_voucher_requirement_option: {
    is_ask_price: boolean;
  };
}

export const loadTendersRegistrationCoreVouchers = async ({
  projectId,
  tenderId,
}: LoadTendersParams): Promise<TenderCoreVoucher> => {
  appLog('loadTendersRegistrationCoreVouchers ->', {
    projectId,
    tenderId,
  });

  const res = await apiCweb.get<TenderCoreVoucher>(
    `/projects/${projectId}/tenders/${tenderId}/core_vouchers`
  );

  return res.data;
};

type TenderUpdateViewModel = {
  title: string | null;
  reference?: string | null;
  description?: string | null;
  active_from?: number | null;
  active_until?: number | null;
  deadline_at: number | null;
  execution_period?: string | null;
  execution_period_data?: number | null;
  qa_start?: number | null;
  qa_stop?: number | null;
  contact_information_id?: number | null;
  location_1?: string | null;
  location_2?: string | null;
  contractor_title?: string | null;
  contractor_subtitle?: string | null;
  results_deadline?: number | null;
  results_details?: string | null;
  share_title: string | null;
  share_description: string | null;
};

export const patchTendersRegistrationInfo = async ({
  projectId,
  tenderId,
  ...rest
}: LoadTendersParams & TenderUpdateViewModel): Promise<DbTender> => {
  appLog('patchTendersRegistrationInfo ->', {
    projectId,
    tenderId,
    ...rest,
  });

  const res = await apiCweb.patch<DbTender>(
    `/projects/${projectId}/tenders/${tenderId}`,
    { ...rest }
  );

  return res.data;
};

export const editTendersLabel = async ({
  projectId,
  tenderId,
  ...rest
}: LoadTendersParams & { label_id: number; value: string | null }) => {
  appLog('editTendersLabel ->', {
    projectId,
    tenderId,
  });

  const res = await apiCweb.patch(
    `/projects/${projectId}/tenders/${tenderId}/labels`,
    { ...rest }
  );

  return res.data;
};

export const editTendersLabels = async ({
  projectId,
  tenderId,
  data,
}: LoadTendersParams & {
  data: { label_id: number; value: unknown | null }[];
}) => {
  appLog('editTendersLabels ->', {
    projectId,
    tenderId,
  });

  const res = await apiCweb.put(
    `/projects/${projectId}/tenders/${tenderId}/labels`,
    data
  );

  return res.data;
};

export const putTenderContractor = async ({
  projectId,
  tenderId,
  contact_key,
}: LoadTendersParams & { contact_key: string }): Promise<DbTender> => {
  appLog('patchTendersRegistrationInfo ->', {
    projectId,
    tenderId,
    contact_key,
  });

  const res = await apiCweb.put<DbTender>(
    `/projects/${projectId}/tenders/${tenderId}/contractor/${contact_key}`,
    {}
  );

  return res.data;
};

export const addTenderContacts = async ({
  projectId,
  tenderId,
  contact_keys,
  temp_old_keys,
}: LoadTendersParams & {
  contact_keys: string[];
  temp_old_keys?: string[];
}): Promise<DbTender> => {
  appLog('patchTendersRegistrationInfo ->', {
    projectId,
    tenderId,
    contact_keys,
  });

  if (temp_old_keys && temp_old_keys.length) {
    const oldData = temp_old_keys?.map((c) => ({ contact_key: c }));
    await apiCweb.delete(
      `/projects/${projectId}/tenders/${tenderId}/contacts`,
      {
        data: oldData,
      }
    );
  }

  const data = contact_keys.map((c) => ({ contact_key: c }));
  const res = await apiCweb.post<DbTender>(
    `/projects/${projectId}/tenders/${tenderId}/contacts`,
    data
  );

  return res.data;
};

export const changeTenderState = async ({ projectId, tenderId, action }) => {
  appLog('changeTenderState ->', {
    projectId,
    tenderId,
    action,
  });

  const res = await apiCweb.put(
    `/projects/${projectId}/tenders/${tenderId}?action=${action}`,
    {}
  );

  return res.data;
};

export const extendOfferSubmissionDeadline = async ({
  projectId,
  tenderId,
  deadline_at,
}) => {
  appLog('extendOfferSubmissionDeadline ->', {
    projectId,
    tenderId,
    deadline_at,
  });

  const res = await apiCweb.put(
    `/projects/${projectId}/tenders/${tenderId}?action=extend`,
    { deadline_at }
  );

  return res.data;
};

export interface TenderModuleSettings {
  system_enabled: boolean;
  project_enabled: boolean;
  current_user_accessible: boolean;
}

export const getTenderSettings = async ({
  projectId,
}: {
  projectId: string;
}): Promise<TenderModuleSettings> => {
  const res = await apiCweb.get<TenderModuleSettings>(
    `/project_settings/${projectId}/tender`
  );

  return res.data;
};

export const updateTenderSettings = async ({
  projectId,
  project_enabled,
}: {
  projectId: string;
  project_enabled: boolean;
}): Promise<void> => {
  await apiCweb.patch(`/project_settings/${projectId}/tender`, {
    project_enabled,
  });
};

export const newSingleUnassignedTenderFolderFileDownload = async (
  key: string,
  { projectId, rootFolderId, folderId, registration_ids, serial }
): Promise<newFileType[]> => {
  appLog('newSingleUnassignedTenderFolderFileDownload #1 ->', {
    projectId,
    rootFolderId,
    folderId,
    registration_ids,
    serial,
  });
  try {
    const res = await apiCweb.get<newFileType[]>(
      `/projects/${projectId}/tender_folders/${rootFolderId}/folders/${folderId}/versioned_files/file_download`,
      {
        params: {
          recursive: true,
          registration_ids: Array.isArray(registration_ids)
            ? registration_ids.join(',')
            : registration_ids,
          skip: 0,
          serial: serial,
        },
        timeout: 30000,
      }
    );

    appLog(`newSingleUnassignedTenderFolderFileDownload LOG #2 -> `, res.data);
    return res.data;
  } catch (error) {
    console.error(`Failed to load file: ${registration_ids}`, error);
    return [];
  }
};

export const getUnassignedTenderFolderURLs = async (
  projectId: number,
  using_keys: string[]
) => {
  appLog('getUnassignedTenderFolderURLs #1 ->', {
    projectId,
    using_keys,
  });
  try {
    const res = await apiCweb.post(
      `/projects/${projectId}/tender_folders/batch_download_urls`,
      { using_keys },
      { timeout: 30000 }
    );

    appLog(`getUnassignedTenderFolderURLs LOG #2 ->`, res.data);
    return res.data;
  } catch (error) {
    return [];
  }
};

export interface LoadTenderSubfoldersParams {
  projectId: string | number;
  tenderId: string | number;
  rootFolderId: string | number;
}

// Fetch tender subfolders for a specific root folder
export const loadTenderSubfolders = async (
  _: any,
  { projectId, tenderId, rootFolderId }: LoadTenderSubfoldersParams
) => {
  appLog('loadTenderSubfolders ->', { projectId, tenderId, rootFolderId });
  const res = await apiCweb.get<DbTenderFolder[]>(
    `/projects/${projectId}/tenders/${tenderId}/tender_folders/${rootFolderId}/folders`
  );

  return res.data;
};

export const unpublishTenderPublic = async ({
  projectId,
  tenderId,
  removed_reason,
}: {
  projectId: string | number;
  tenderId: string | number;
  removed_reason?: string;
}) => {
  appLog('unpublishTenderPublic ->', {
    projectId,
    tenderId,
    removed_reason,
  });

  const res = await apiCweb.delete(
    `/projects/${projectId}/tenders/${tenderId}/public`,
    {
      params: {
        removed_reason,
      },
    }
  );

  return res.data;
};
